import { apiClient } from '@littlebird/auth-client';
import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { toast } from 'sonner';

import {
  inputsDbAtoms,
  type InputsDbFilters,
  type InputsDbResponse,
} from './inputs-db.atoms';

export interface RawInputNode {
  text: string;
  text_size: number;
  is_original: boolean;
  created_at: string;
}

export interface TruncationSummary {
  original_size?: number;
  final_size?: number;
  characters_removed?: number;
}

export interface RawInputChainResponse {
  raw_input_chain: RawInputNode[];
  was_truncated: boolean;
  truncation_summary: TruncationSummary | null;
  raw_inputs_deleted: boolean;
}

export const useInputsDbData = () => {
  const filters = inputsDbAtoms.useFilters();

  return useQuery({
    queryKey: ['inputsDb', filters],
    queryFn: async ({ signal }): Promise<InputsDbResponse> => {
      try {
        const params = new URLSearchParams({
          limit: '50',
          offset: '0',
        });

        if (filters.type.length > 0) {
          filters.type.forEach((type) => params.append('tables', type));
        }
        if (filters.app) {
          params.append('app', filters.app);
        }
        if (filters.search.trim()) {
          params.append('search', filters.search.trim());
        }
        if (filters.ckJobId.trim()) {
          params.append('ck_job_id', filters.ckJobId.trim());
        }
        if (filters.embedded !== undefined) {
          params.append('embedded', filters.embedded.toString());
        }
        if (filters.after) {
          params.append('after', filters.after);
        }
        if (filters.before) {
          params.append('before', filters.before);
        }
        if (filters.isHybridSearch) {
          params.append('isHybridSearch', filters.isHybridSearch.toString());
        }
        params.append('sortBy', filters.sortBy);

        const response = await apiClient.get<InputsDbResponse>(
          `/debug/user_context/?${params.toString()}`,
          { signal }
        );

        return response;
      } catch (error) {
        if (signal.aborted) {
          throw error;
        }
        console.error('Failed to fetch inputs db data:', error);
        toast.error('Error', {
          description: `Failed to fetch data: ${error}`,
        });
        throw error;
      }
    },
    enabled: filters.type.length > 0,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    refetchOnWindowFocus: true,
    staleTime: 0,
    gcTime: 0,
  });
};

export const useUpdateFilters = () => {
  return useCallback((updater: Partial<InputsDbFilters>) => {
    inputsDbAtoms.updateFilters(updater);
  }, []);
};

export const useResetFilters = () => {
  return useCallback(() => {
    inputsDbAtoms.resetFilters();
  }, []);
};

export const useRawInputChain = (inputId: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['rawInputChain', inputId],
    queryFn: async ({ signal }): Promise<RawInputChainResponse> => {
      try {
        const response = await apiClient.get<RawInputChainResponse>(
          `/debug/input/${inputId}/raw-input-chain`,
          { signal }
        );

        return response;
      } catch (error) {
        if (signal.aborted) {
          throw error;
        }
        console.error('Failed to fetch raw input chain:', error);
        toast.error('Error', {
          description: `Failed to fetch diff data: ${error}`,
        });
        throw error;
      }
    },
    enabled: enabled && !!inputId,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};
